import { Button, Typography } from "@suraasa/placebo-ui"
import { theme, useMediaQuery } from "@suraasa/placebo-ui-legacy"
import clsx from "clsx"
import PageContainer from "components/PageContainer"
import AssessmentFooter from "features/Cpat/Assessments/Attempt/AssessmentFooter"
import Question from "features/Cpat/Assessments/Attempt/Question"
import QuestionPanel from "features/Cpat/Assessments/Attempt/QuestionPanel"
import Navbar from "features/Cpat/Assessments/Navbar"
import { CpatQuestionnaireTourStatus } from "features/Cpat/CpatQuestionnaireOnboarding/utils/tourManager"
import { startCpatAssessment } from "features/Cpat/helpers"
import { useSuraasaPlusContext } from "features/SuraasaPlus/context"
import { useState } from "react"
import Joyride, { Step } from "react-joyride"
import { useNavigate } from "react-router-dom"
import routes from "routes"
import { BROWSER_STORAGE_KEYS } from "utils/constants"
import useHideGlobalBottomNavigation from "utils/hooks/useGlobalHideBottomNavigation"
import useHideTopNavigation from "utils/hooks/useHideTopNavigation"

import { SpnyTourEnum, useSuraasaPlusNewYear } from "../Context"
import { tourManager } from "../TourManager"
import AllSet from "./AllSet"
import FakeRecording from "./assets/fake-recording.webp"
import styles from "./Attempt.module.css"
import { ASSESSMENT_TOUR_TARGETS } from "./constant"
import { dummyQuestionBank } from "./data"
import GetStarted from "./GetStarted"
import OnboardingStep from "./OnboardingStep"

const CpatAssessmentTour = () => {
  useHideTopNavigation()
  useHideGlobalBottomNavigation()
  const navigate = useNavigate()
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"))
  const fakeWarningCount = 8
  const { isSPNYUser } = useSuraasaPlusContext()
  const { cpatQuestionnaire, spnyCpatDashboardTour } = useSuraasaPlusNewYear()

  // const { attemptId, isProctored } = Object.fromEntries(
  //   new URLSearchParams(window.location.search)
  // )
  const searchParams = new URLSearchParams(window.location.search)
  const attemptId = searchParams.get("attemptId")
  const isProctored = searchParams.get("isProctored")

  const questionBank = dummyQuestionBank
  const currentQuestion = questionBank[0]
  const [openDialog, setOpenDialog] = useState<"start" | "finish" | null>(
    "start"
  )
  const [step, setStep] = useState<"joyride" | null>(null)

  const steps: Step[] = [
    {
      target: `.${ASSESSMENT_TOUR_TARGETS.proctoring}`,
      content: (
        <div>
          <div className="p-2.5">
            <Typography variant="title3" className="mb-1 text-start">
              How It Works
            </Typography>

            <Typography
              variant="smallBody"
              className="text-start text-onSurface-600"
            >
              This assessment requires camera access to verify your session. The
              system may flag the session if:
            </Typography>
            <ul className="list-disc pl-2 pt-1 text-smallBody text-onSurface-600">
              <li>No face is detected</li>
              <li>Multiple faces are detected</li>
              <li>You switch tabs</li>
              <li>You exit full screen</li>
            </ul>
          </div>
        </div>
      ),
      disableBeacon: true,
    },
    {
      target: `.${ASSESSMENT_TOUR_TARGETS.currentQuestion}`,
      content: (
        <div>
          <div className="p-2.5">
            <Typography variant="title3" className="mb-1 text-start">
              Your Questions Appear Here
            </Typography>
            <Typography
              variant="smallBody"
              className="text-start text-onSurface-600"
            >
              This is where each question is displayed. Your answer saves
              automatically when you select an option.
            </Typography>
          </div>
        </div>
      ),
      disableBeacon: true,
    },
    {
      target: `.${ASSESSMENT_TOUR_TARGETS.questionPanel}`,
      content: (
        <div>
          <div className="p-2.5">
            <Typography variant="title3" className="mb-1 text-start">
              Track Your Questions
            </Typography>
            <Typography
              variant="smallBody"
              className="text-start text-onSurface-600"
            >
              Tap this to open the question sheet. It shows all questions and
              their status — Answered, Not Answered, and Not Visited.
            </Typography>
          </div>
        </div>
      ),
      placement: "left",
      disableBeacon: true,
    },
    {
      target: `.${ASSESSMENT_TOUR_TARGETS.trackTime}`,
      content: (
        <div>
          <div className="p-2.5">
            <Typography variant="title3" className="mb-1 text-start">
              Track Your Time
            </Typography>
            <Typography
              variant="smallBody"
              className="text-start text-onSurface-600"
            >
              This is a 60-minute timed assessment. Your remaining time appears
              here.
            </Typography>
          </div>
        </div>
      ),
      disableBeacon: true,
    },
    {
      target: `.${ASSESSMENT_TOUR_TARGETS.submit}`,
      content: (
        <div>
          <div className="p-2.5">
            <Typography variant="title3" className="mb-1 text-start">
              Submit Assessment
            </Typography>
            <Typography
              variant="smallBody"
              className="text-start text-onSurface-600"
            >
              When you are ready to finish, click submit.{" "}
              <span className="font-semibold text-onSurface-600">
                Note: You cannot change your answers after you submit.
              </span>
            </Typography>
          </div>
        </div>
      ),
      disableBeacon: true,
    },
  ]

  const handleComplete = () => {
    setStep(null)
    setOpenDialog("finish")
  }

  const handleJoyrideCallback = data => {
    const { status, lifecycle } = data

    const tourCompletedStatuses = ["finished"]
    if (tourCompletedStatuses.includes(status) && lifecycle === "complete") {
      handleComplete()
    }
  }
  if (!isSPNYUser) {
    navigate(routes.cpat.dashboard)
    return null
  }
  const handleDialog = () => {
    if (spnyCpatDashboardTour !== SpnyTourEnum.completed) {
      tourManager.completed(BROWSER_STORAGE_KEYS.spnyCpatDashboardTour)
    }

    if (cpatQuestionnaire !== CpatQuestionnaireTourStatus.completed) {
      tourManager.completed(
        BROWSER_STORAGE_KEYS.cpatOnboardingQuestionnaireStatus
      )
    }

    setOpenDialog(null)
    tourManager.completed(BROWSER_STORAGE_KEYS.cpatTour)
  }
  return (
    <>
      <GetStarted
        open={openDialog === "start"}
        handleClose={() => {
          handleDialog()
          navigate(routes.cpat.dashboard)
        }}
        handleContinue={() => {
          setStep("joyride")
          setOpenDialog(null)
          tourManager.initiated(BROWSER_STORAGE_KEYS.cpatTour)
        }}
      />
      <AllSet
        open={openDialog === "finish"}
        handleClose={() => {
          handleDialog()
          navigate(routes.cpat.dashboard)
        }}
        handleContinue={() => {
          handleDialog()

          if (attemptId) {
            startCpatAssessment({
              attemptId: attemptId,
              isProctored: isProctored === "true" ? true : false,
            })
          }
        }}
      />
      <Joyride
        steps={steps}
        run={step === "joyride"}
        tooltipComponent={props => (
          <OnboardingStep
            toolTipRenderProps={props}
            onComplete={handleComplete}
          />
        )}
        continuous
        disableOverlayClose
        disableScrolling
        disableCloseOnEsc
        hideBackButton
        hideCloseButton
        callback={handleJoyrideCallback}
      />
      <div className="h-full">
        <Navbar
          navBarTitle="Comprehensive Pedagogy Assessment of Teachers"
          gutterBottom={false}
          slotEnd={
            <div className="grid grid-flow-col items-center gap-2">
              <div
                className={clsx(
                  "grid items-end",
                  ASSESSMENT_TOUR_TARGETS.trackTime
                )}
              >
                <Typography>53:52</Typography>
                <Typography variant="smallBody" className="text-onSurface-500">
                  Time left
                </Typography>
              </div>
              <div className={ASSESSMENT_TOUR_TARGETS.submit}>
                <Button disabled size="sm" color="black">
                  Submit
                </Button>
              </div>
            </div>
          }
        />
        <div
          className={clsx(
            "mt-[62px] bg-[#040122] px-1.5 py-0.75",
            {
              [styles.none]: !isMdDown,
            },
            ASSESSMENT_TOUR_TARGETS.proctoring
          )}
        >
          <PageContainer>
            <div className="flex flex-row items-stretch justify-between gap-2">
              <div className="flex items-center">
                <Typography
                  variant="strongSmallBody"
                  className="mr-0.5 w-fit shrink-0 text-white"
                >
                  Recording
                </Typography>
                <div className="flex w-fit shrink-0 flex-row items-center gap-0.5 rounded-full bg-white px-1 py-0.25">
                  <Typography variant="strongSmallBody">On</Typography>{" "}
                  <div className="size-1.5 rounded-full bg-ito-mint-700" />
                </div>
              </div>
              {/* <div className="block w-0.25 rounded-full bg-white sm:hidden" /> */}
              {/* <div className="flex items-center sm:shrink-0">
                 <Typography
                  variant="strongSmallBody"
                  className="text-white sm:mr-2 sm:shrink-0"
                >
                  0/8 violations triggered
                </Typography>
                <div className="relative hidden h-1 w-[171px] overflow-hidden rounded-full bg-white sm:block">
                  <div
                    style={{
                      background:
                        "linear-gradient(90deg, #F2D16A 0%, #CE3E3E 132.38%)",
                    }}
                    className="absolute left-0 top-0 size-full max-w-[60%]"
                  />
                </div>
              </div> */}
            </div>
          </PageContainer>
        </div>
        <div className={clsx("h-full pt-7")}>
          <div
            className={clsx({
              [styles.none]: !isMdDown,
            })}
          >
            {/* {fakeWarningCount !== null && <CPATWebcam videoRef={videoRef} />} */}
          </div>
          <div className="grid gap-1">
            <div className={`${styles.content} row-span-1 grid-cols-5`}>
              {fakeWarningCount !== null && !isMdDown && (
                <div
                  className={clsx("col-span-1", {
                    [styles.none]: isMdDown,
                  })}
                >
                  <div className={ASSESSMENT_TOUR_TARGETS.proctoring}>
                    <div className="grid gap-3 p-1 pt-0 xl:pl-4">
                      <div className="relative">
                        <img
                          src={FakeRecording}
                          alt="fake-recording"
                          className="mx-auto h-[118px] w-[194px] shrink-0"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div
                className={clsx(
                  "col-span-5 lg:col-span-3",
                  ASSESSMENT_TOUR_TARGETS.currentQuestion
                )}
              >
                <div className={clsx(styles.test, "grid")}>
                  {
                    <Question
                      question={currentQuestion}
                      handleResponse={() => {}}
                    />
                  }
                </div>
              </div>

              {!isMdDown && (
                <div
                  className={clsx(
                    "col-span-1",
                    ASSESSMENT_TOUR_TARGETS.questionPanel
                  )}
                >
                  <QuestionPanel
                    onQuestionChange={() => {}}
                    questions={questionBank}
                    currentQuestion={currentQuestion}
                    onClose={() => {}}
                  />
                </div>
              )}
            </div>
            {
              <div
                className={clsx(
                  "grid grid-cols-5 gap-7",
                  styles.footerContainer
                )}
              >
                <div
                  className={clsx(
                    "col-span-5 lg:col-span-3 lg:col-start-2 lg:pl-1",
                    ASSESSMENT_TOUR_TARGETS.questionPanel,
                    {}
                  )}
                >
                  <AssessmentFooter
                    onQuestionChange={() => {}}
                    onNext={() => {}}
                    onPrevious={() => {}}
                    questions={questionBank}
                    currentQuestion={currentQuestion}
                  />
                </div>
              </div>
            }
          </div>
        </div>
      </div>
    </>
  )
}

export default CpatAssessmentTour
